import React from 'react'

import clsx from 'clsx'

import ActionButton, {
  ActionButtonSize,
  ActionButtonType,
} from '$extensionSrc/components/ActionButton'
import StatusDot from '$extensionSrc/components/ImpactMetrics/StatusDot'
import {
  getStatusDotColorForStatus,
  ScoreStatus,
} from '$extensionSrc/components/ImpactMetrics/types'
import ImpressionLogger from '$extensionSrc/components/ImpressionLogger'
import LogContextProvider from '$extensionSrc/components/LogContextProvider'

import styles from './KeyScoreSummary.module.scss'
import { ChecklistSummary } from './types'

function onlyCapitalizeFirstLetter(str: string) {
  if (!str) {
    return str
  }

  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

export default function KeyScoreSummary({
  category,
  checklistSummary,
  ctaText,
  highlights,
  onClickCta,
  status,
  todos,
}: {
  category: string
  checklistSummary: ChecklistSummary
  ctaText: string
  highlights: string[]
  onClickCta: () => void
  status: ScoreStatus
  todos: string[]
}) {
  const logEventProps = {
    keyScoreCategory: category,
    keyScoreStatus: status,
    numHighlights: highlights?.length,
    numTodos: todos?.length,
    numChecklistUpdatedOrFulfilled: checklistSummary?.numUpdatedOrFulfilled,
    numChecklistSkipped: checklistSummary?.numSkipped,
    numChecklistIncomplete: checklistSummary?.numIncomplete,
    component: 'KeyScoreSummary',
  }

  return (
    <LogContextProvider {...logEventProps}>
      <ImpressionLogger eventName="component:render" eventProps={{}}>
        <div className="KeyScoreSummary">
          <div className={styles.header}>
            <div className={styles.status}>
              <span className="heading3 bold">
                {onlyCapitalizeFirstLetter(category)}:
              </span>
              <StatusDot color={getStatusDotColorForStatus(status)} />
              <span className="heading3">{status}</span>
            </div>
            <ActionButton
              className={clsx(
                styles.cta,
                status !== 'High' && 'button-glow-shadow',
              )}
              size={ActionButtonSize.SMALL}
              type={ActionButtonType.SECONDARY}
              onClick={onClickCta}
            >
              {ctaText}
            </ActionButton>
          </div>
          <div className={styles.details}>
            <div className={clsx(styles.checklistProgress)}>
              <span className="body1">
                <strong>Your recommendations: </strong>
                {[
                  `${checklistSummary.numUpdatedOrFulfilled} complete`,
                  `${checklistSummary.numSkipped} skipped`,
                  `${checklistSummary.numIncomplete} left to do`,
                ].join(', ')}
              </span>
            </div>
            {highlights.length > 0 && (
              <div className={styles.highlights}>
                <ul className={styles.list}>
                  {highlights.map((x) => (
                    <li className="body1" key={x}>
                      {x}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {todos.length > 0 && (
              <div className={styles.todos}>
                <span className="body1">Consider reviewing:</span>
                <ul className={styles.list}>
                  {todos.map((x) => (
                    <li className="body1" key={x}>
                      {x}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </ImpressionLogger>
    </LogContextProvider>
  )
}
